import * as PIXI from "pixi.js";

import { FreespinCounterView } from "@src/game/screens/components/FreespinCounterView";
import { FreespinIntroView } from "@src/game/screens/components/FreespinIntroView";
import { FreespinOutroView } from "@src/game/screens/components/FreespinOutroView";
import { BonusBackground } from "@src/game/screens/backgrounds/BonusBackground";
import { CoinAnimator } from "@src/game/symbol-panel/components/CoinAnimator";
import { Assets, TAssetAlias, TAssetData } from "@src/core/assets/Assets";
import { EGameState, TSpinResponse } from "@src/server/ResponseTypes";
import { SymbolPanel } from "@src/game/symbol-panel/SymbolPanel";
import { WinView } from "@src/game/screens/components/WinView";
import { assetManifest } from "@src/game/assets/AssetImports";
import { CellPanel } from "@src/game/cell-panel/CellPanel";
import { EventSystem } from "@src/core/events/EventSystem";
import { Symbol } from "@src/game/symbol-panel/Symbol";
import { gameEngine } from "@src/game/GameEngine";
import { Screen } from "@src/core/screen/Screen";
import { constants } from "@src/game/Constants";
import { GameUI } from "@src/game/ui/GameUI";
import {
  EJackpotTier,
  JackpotTierView,
} from "@src/game/screens/components/JackpotTierView";
import { Spine } from "@pixi/spine-pixi";
import { TDeviceType } from "@src/index";
import gsap from "gsap";
import { Audio } from "@src/core/audio/Audio";
import { Cell } from "../cell-panel/Cell";

// Workaround to get asset name completion to work properly
type TNonLoadingAsset<T extends string> = T extends `${string}loading${string}`
  ? never
  : T extends `${string}Loading${string}`
  ? never
  : T extends `loading${string}`
  ? never
  : T extends `Loading${string}`
  ? never
  : T;

export type TGameAssetAlias = TNonLoadingAsset<keyof typeof assetManifest>;

export class GameScreen extends Screen {
  public get assets(): Record<TAssetAlias, TAssetData> {
    return Object.fromEntries(
      Object.entries(assetManifest).filter(
        ([alias, _]) => !alias.toLowerCase().includes("loading"),
      ),
    ) as Record<TAssetAlias, TAssetData>;
  }

  private _logo!: Spine;
  private _backgroundHorizontalMain!: PIXI.Sprite;

  private _symbolPanel!: SymbolPanel;
  private _cellPanel!: CellPanel;

  private _gameUi!: GameUI;
  private _winView!: WinView;
  private _freespinIntroView!: FreespinIntroView;
  private _freespinOutroView!: FreespinOutroView;
  private _freespinCounterView!: FreespinCounterView;
  private _jackpotTierViews: JackpotTierView[] = [];
  private _coinAnimator!: CoinAnimator;
  private _bonusBackground!: BonusBackground;
  private _holdWinContainer!: PIXI.Container;
  private _holdWinText!: PIXI.Text;
  private _holdWinPanel!: PIXI.Sprite;

  public static currentBgAudioName: string;

  private _isLandscape!: boolean;

  protected onLoadedAssets(): void {
    super.onLoadedAssets();

    this._setupEventListeners();

    this._initializeAssets();
    this._parentAssets();

    this._initializeComponents();
    this._parentComponents();

    this._initializeScreenClickableArea();
  }

  public playMainBgMusic(): void {
    GameScreen.currentBgAudioName = "sndBackground";

    Audio.play("sndBackground", true);
    Audio.volume("sndBackground", 0.0);
    Audio.fade("sndBackground", 1.0, 2.0);

    Audio.fade("sndBonusBackground", 0.0, 1.0, () => {
      Audio.stop("sndBonusBackground");
    });
    Audio.fade("sndFSIdle", 0.0, 1.0, () => {
      Audio.stop("sndFSIdle");
    });
    Audio.fade("sndFSBackground", 0.0, 1.0, () => {
      Audio.stop("sndFSBackground");
    });
    Audio.fade("sndFSSummary", 0.0, 1.0, () => {
      Audio.stop("sndFSSummary");
    });
  }

  private playBonusBgMusic(): void {
    GameScreen.currentBgAudioName = "sndBonusBackground";

    Audio.fade("sndBackground", 0.0, 1.0, () => {
      Audio.stop("sndBackground");
    });

    Audio.play("sndBonusBackground", true);
    Audio.volume("sndBonusBackground", 0.0);
    Audio.fade("sndBonusBackground", 1.0, 2.0);

    Audio.fade("sndFSIdle", 0.0, 1.0, () => {
      Audio.stop("sndFSIdle");
    });
    Audio.fade("sndFSBackground", 0.0, 1.0, () => {
      Audio.stop("sndFSBackground");
    });
    Audio.fade("sndFSSummary", 0.0, 1.0, () => {
      Audio.stop("sndFSSummary");
    });
  }

  private playFSIdleMusic(): void {
    GameScreen.currentBgAudioName = "sndFSIdle";

    Audio.fade("sndBackground", 0.0, 1.0, () => {
      Audio.stop("sndBackground");
    });
    Audio.fade("sndBonusBackground", 0.0, 1.0, () => {
      Audio.stop("sndBonusBackground");
    });

    Audio.play("sndFSIdle", true);
    Audio.volume("sndFSIdle", 0.0);
    Audio.fade("sndFSIdle", 1.0, 2.0);

    Audio.fade("sndFSBackground", 0.0, 1.0, () => {
      Audio.stop("sndFSBackground");
    });
    Audio.fade("sndFSSummary", 0.0, 1.0, () => {
      Audio.stop("sndFSSummary");
    });
  }

  private playFSBgMusic(): void {
    GameScreen.currentBgAudioName = "sndFSBackground";

    Audio.fade("sndBackground", 0.0, 1.0, () => {
      Audio.stop("sndBackground");
    });
    Audio.fade("sndBonusBackground", 0.0, 1.0, () => {
      Audio.stop("sndBonusBackground");
    });
    Audio.fade("sndFSIdle", 0.0, 1.0, () => {
      Audio.stop("sndFSIdle");
    });

    Audio.play("sndFSBackground", true);
    Audio.volume("sndFSBackground", 0.0);
    Audio.fade("sndFSBackground", 1.0, 2.0);

    Audio.fade("sndFSSummary", 0.0, 1.0, () => {
      Audio.stop("sndFSSummary");
    });
  }

  private playFSSummaryMusic(): void {
    GameScreen.currentBgAudioName = "sndFSSummary";

    Audio.fade("sndBackground", 0.0, 1.0, () => {
      Audio.stop("sndBackground");
    });
    Audio.fade("sndBonusBackground", 0.0, 1.0, () => {
      Audio.stop("sndBonusBackground");
    });
    Audio.fade("sndFSIdle", 0.0, 1.0, () => {
      Audio.stop("sndFSIdle");
    });
    Audio.fade("sndFSBackground", 0.0, 1.0, () => {
      Audio.stop("sndFSBackground");
    });

    Audio.play("sndFSSummary", false);
    Audio.volume("sndFSSummary", 0.0);
    Audio.fade("sndFSSummary", 1.0, 2.0);
  }

  private _setupEventListeners(): void {
    EventSystem.addListener(
      constants.GameEvents.LANDSCAPE_LAYOUT_REQUESTED,
      this._onLandscapeLayoutRequested.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.PORTRAIT_LAYOUT_REQUESTED,
      this._onPortraitLayoutRequested.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.DEVICE_CHANGED,
      this._onDeviceChanged.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.GAME_PERFORM_ACTION,
      this._onPerformedGameAction.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.REEL_PANEL_SPIN_STOPPED,
      this._onReelPanelSpinStopped.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.CELL_PANEL_SPIN_STOPPED,
      this._onCellPanelSpinStopped.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.CELL_PANEL_SHOW_ANIMATION_STARTED,
      this._onCellPanelShowAnimationStarted.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.WINNING_POPUP_CLOSED,
      this._onWinningPopupClosed.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.FREESPIN_INTRO_OPEN_ANIMATION_STARTED,
      this._onFreespinIntroOpenAnimationStarted.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.FREESPIN_INTRO_OPEN_ANIMATION_ENDED,
      this._onFreespinIntroOpenAnimationEnded.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.FREESPIN_INTRO_CLOSED,
      this._onFreespinIntroClosed.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.FREESPIN_OUTRO_CLOSED,
      this._onFreespinOutroClosed.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_PANEL_SHOWN,
      this._onSymbolPanelShown.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_PANEL_SHOW_ANIMATION_STARTED,
      this._onSymbolPanelShowAnimationStarted.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_PANEL_HIDE_ANIMATION_STARTED,
      this._onSymbolPanelHideAnimationStarted.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_PANEL_HIDDEN,
      this._onSymbolPanelHidden.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.CELL_PANEL_SHOWN,
      this._onCellPanelShown.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.CELL_PANEL_HIDE_ANIMATION_STARTED,
      this._onCellPanelHideAnimationStarted.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.CELL_PANEL_HIDDEN,
      this._onCellPanelHidden.bind(this),
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_INTERACTABLE_AREA,
      this._onClickedInteractableArea.bind(this),
    );
  }

  private _onPerformedGameAction(_: TSpinResponse): void {
    switch (gameEngine.gameActionData.state) {
      case EGameState.HOLD_TO_BONUS:
      case EGameState.BONUS:
      case EGameState.BONUS_TO_MAIN:
        this._freespinCounterView.setCounterText(
          gameEngine.gameActionData.freespins.toString(),
        );
        break;
      case EGameState.MAIN_TO_HOLD:
      case EGameState.BONUS_TO_HOLD:
      case EGameState.HOLD_TO_MAIN:
      case EGameState.HOLD:
        // this._freespinCounterView.setCounterText(
        //   gameEngine.gameActionData.holdspins.toString(),
        // );
        break;
    }
  }

  // Either move to freespin when there are no wins, or
  // when the win popup is closed.
  private _onReelPanelSpinStopped(): void {
    switch (gameEngine.gameActionData.state) {
      case EGameState.MAIN_TO_BONUS:
        if (gameEngine.gameActionData.wins.length === 0) {
          gsap.delayedCall(1.5, () => {
            this._symbolPanel.playHideAnimation();
          });
        }
        break;
      case EGameState.BONUS_TO_MAIN:
        if (gameEngine.gameActionData.wins.length === 0) {
          this._symbolPanel.playHideAnimation();
        }
        break;
      case EGameState.MAIN_TO_HOLD:
        if (gameEngine.gameActionData.wins.length === 0) {
          this._symbolPanel.playHideAnimation();
        }
        break;
      case EGameState.HOLD_TO_MAIN:
        if (gameEngine.gameActionData.wins.length === 0) {
          this._cellPanel.playHideAnimation();
        }
        break;
    }
  }

  private _onCellPanelSpinStopped(): void {
    this._freespinCounterView.setCounterText(
      gameEngine.gameActionData.holdspins.toString()
    );

    this._setHoldMultiplierValue();
  }

  private _onWinningPopupClosed(): void {
    switch (gameEngine.gameActionData.state) {
      case EGameState.MAIN_TO_BONUS:
        this._freespinIntroView.playShowAnimation();
        this._symbolPanel.playHideAnimation();
        break;
      case EGameState.BONUS_TO_MAIN:
        this._symbolPanel.playHideAnimation();
        break;
      case EGameState.MAIN_TO_HOLD:
        this._symbolPanel.playHideAnimation();
        break;
      case EGameState.HOLD_TO_MAIN:
        this._cellPanel.playHideAnimation();
        break;
    }
  }

  private _onFreespinIntroOpenAnimationStarted(): void { }
  private _onFreespinIntroOpenAnimationEnded(): void { }

  private _onFreespinIntroClosed(): void {
    // this._freespinCounterView.setCounterText(
    //   gameEngine.gameActionData.freespins.toString(),
    // );
    this._symbolPanel.playShowAnimation();
  }

  private _onFreespinOutroClosed(): void {
    this._symbolPanel.playShowAnimation();
  }

  private _onSymbolPanelShown(): void {

    if (gameEngine.gameActionData) {
      switch (gameEngine.gameActionData.state) {
        case EGameState.HOLD_TO_MAIN:
        case EGameState.HOLD:
          this.playMainBgMusic();
          break;
        case EGameState.BONUS_TO_MAIN:
        case EGameState.MAIN:
          this.playMainBgMusic();
          break;
        case EGameState.MAIN_TO_BONUS:
        case EGameState.BONUS:
          console.log("Setting freespin counter visible = true");
          this._freespinCounterView.view.visible = true;
          this.playFSBgMusic();
          break;
      }
    }
  }

  private _onSymbolPanelHidden(): void {
    if (gameEngine.gameActionData) {
      switch (gameEngine.gameActionData.state) {
        case EGameState.MAIN_TO_BONUS:
          this._freespinIntroView.playShowAnimation();
          this.playFSIdleMusic();
          break;
        case EGameState.BONUS_TO_MAIN:
          this._freespinOutroView.playShowAnimation();
          this.playFSSummaryMusic();
          break;
        case EGameState.MAIN_TO_HOLD:
          this._cellPanel.playShowAnimation();
          break;
      }
    }

    const ratio: number = window.innerWidth / window.innerHeight;
    // Second max value seems to do jack shit
    const k: number = Math.max(1.75 / ratio, 1.0);

    if (gameEngine.gameActionData) {
      switch (gameEngine.gameActionData.state) {
        case EGameState.MAIN_TO_HOLD:
        case EGameState.HOLD_TO_MAIN:
        case EGameState.HOLD:
          if (this._isLandscape) {
            this._freespinCounterView.view.position.set(300, -420);
            this._freespinCounterView.view.scale.set(0.275);
            this._holdWinContainer.position.set(-5, -420);
            this._holdWinContainer.scale.set(0.275);

            this._logo.scale.set(0.1);
            this._logo.position.set(-770 / k, -400);
          } else {
            this._freespinCounterView.view.position.set(-310, 450);
            this._freespinCounterView.view.scale.set(0.35);
            this._holdWinContainer.position.set(-5, 450);
            this._holdWinContainer.scale.set(0.35);

            let baseY = -850;
            const centerY = -200;
            const transitionThreshold = 0.65;

            let logoY = baseY;
            let logoScale = 0.11;

            if (ratio <= transitionThreshold) {
              // Calculate how far below 0.4 we are (0 = at threshold, 1 = at ratio 0)
              const progress = Math.min(
                1,
                (transitionThreshold - ratio) / transitionThreshold,
              );
              // Interpolate between baseY and centerY
              logoY = baseY + (centerY - baseY) * progress;
              logoScale = 0.11 + (0.01 - 0.11) * progress;
            }

            gsap.to(this._logo, {
              x: 5,
              y: logoY,
              duration: 0.5,
              ease: "power2.out",
            });
            this._logo.scale.set(logoScale);
          }
          this._freespinCounterView.setCounterText(
            gameEngine.gameActionData.holdspins.toString(), true
          );
          this._freespinCounterView.view.visible = true;
          this._holdWinContainer.visible = true;

          break;
      }
    }
  }

  private _onSymbolPanelShowAnimationStarted(): void {
    this._holdWinContainer.visible = false;
    this.holdMultiplier = 0.0;
    this._holdWinText.text = "WON\nX0.00";

    if (gameEngine.gameActionData) {
      switch (gameEngine.gameActionData.state) {
        case EGameState.MAIN_TO_BONUS:
          this._showBonusBackground();
          break;
        case EGameState.BONUS_TO_MAIN:
          this._showMainBackground();
          break;
      }
    }

    const ratio: number = window.innerWidth / window.innerHeight;
    // Second max value seems to do jack shit
    const k: number = Math.max(1.75 / ratio, 1.0);

    if (gameEngine.gameActionData) {
      if (this._isLandscape) {
        this._freespinCounterView.view.position.set(-300, -330);
        this._freespinCounterView.view.scale.set(0.275);

        this._logo.scale.set(0.1);
        this._logo.position.set(-770 / k, -400);
      } else {
        this._freespinCounterView.view.position.set(-310, 450);
        this._freespinCounterView.view.scale.set(0.35);

        let baseY = -600;
        const centerY = -200;
        const transitionThreshold = 0.65;

        let logoY = baseY;
        let logoScale = 0.11;

        if (ratio <= transitionThreshold) {
          // Calculate how far below 0.4 we are (0 = at threshold, 1 = at ratio 0)
          const progress = Math.min(
            1,
            (transitionThreshold - ratio) / transitionThreshold,
          );
          // Interpolate between baseY and centerY
          logoY = baseY + (centerY - baseY) * progress;
          logoScale = 0.11 + (0.01 - 0.11) * progress;
        }

        gsap.to(this._logo, {
          x: 5,
          y: logoY,
          duration: 0.5,
          ease: "power2.out",
        });
        this._logo.scale.set(logoScale);
      }
    }
  }

  private _onSymbolPanelHideAnimationStarted(): void {
    if (gameEngine.gameActionData) {
      switch (gameEngine.gameActionData.state) {
        case EGameState.BONUS_TO_MAIN:
          console.log("Setting freespin counter visible = false");
          this._freespinCounterView.view.visible = false;
          break;
        case EGameState.MAIN_TO_HOLD:
          Audio.play("sndBonusTrigger");
          break;
      }
    }
  }

  private _onCellPanelShowAnimationStarted(): void {
    gsap.delayedCall(0.1, () => {
      this._setHoldMultiplierValue(true);
    });
  }

  private holdMultiplier: number = 0.0;

  private _setHoldMultiplierValue(instant: boolean = false): void {
    // Backend gives us the raw multiplier sum in coins, 
    // but the actual "Win" is multiplier * bet.
    let targetMultiplierSum = 0;
    gameEngine.gameActionData.coins.forEach(coin => {
      targetMultiplierSum += coin.multiplier;
    });


    if (instant) {
      this.holdMultiplier = targetMultiplierSum;
      this._holdWinText.text = `WON\nX${gameEngine.formatter.format(this.holdMultiplier)}`;
      return;
    }

    const proxy = { value: this.holdMultiplier };

    gsap.to(proxy, {
      value: targetMultiplierSum,
      duration: 0.6,
      ease: "power2.out",
      onUpdate: () => {
        // Formatter handles the currency symbol/decimals
        this._holdWinText.text = `WON\nX${gameEngine.formatter.format(proxy.value)}`;
      },
      onComplete: () => {
        this.holdMultiplier = targetMultiplierSum;
      }
    });

    this._holdWinText.scale = 1.0;

    const hasChanged = targetMultiplierSum > this.holdMultiplier;

    if (!hasChanged) {
      return;
    }

    gsap.to(this._holdWinText.scale, {
      x: 1.2,
      y: 1.2,
      duration: 0.3,
      ease: "power1.out",
      repeat: 1,
      yoyo: true,
    });
  }

  private _onCellPanelShown(): void {
    this.playBonusBgMusic();
  }

  private _onCellPanelHideAnimationStarted(): void {
    if (gameEngine.gameActionData) {
      switch (gameEngine.gameActionData.state) {
        case EGameState.HOLD_TO_MAIN:
          this._freespinCounterView.view.visible = false;
          this._holdWinContainer.visible = false;
          break;
      }
    }
  }

  private _onCellPanelHidden(): void {
    if (gameEngine.gameActionData) {
      switch (gameEngine.gameActionData.state) {
        case EGameState.HOLD_TO_MAIN:
          this._symbolPanel.playShowAnimation();
          this._freespinCounterView.view.visible = false;
          break;
      }
    }
  }

  private _onClickedInteractableArea(): void {
    if (gameEngine.gameActionData) {
      switch (gameEngine.gameActionData.state) {
        case EGameState.MAIN_TO_BONUS:
          this._freespinIntroView.playHideAnimation();
          break;
        case EGameState.BONUS_TO_MAIN:
          this._freespinOutroView.playHideAnimation();
          break;
      }
    }
  }

  private _showMainBackground(): void {
    gsap.to(this._backgroundHorizontalMain, { alpha: 1.0, duration: 0.75 });
    this._bonusBackground.fadeOut();
  }

  private _showBonusBackground(): void {
    gsap.to(this._backgroundHorizontalMain, { alpha: 0.0, duration: 0.75 });
    this._bonusBackground.fadeIn();
  }

  private _initializeAssets(): void {
    this._backgroundHorizontalMain = new PIXI.Sprite(
      Assets.getAsset<TGameAssetAlias>("mainBgHorizontalImg"),
    );
    this._backgroundHorizontalMain.anchor.set(0.5);

    this._logo = Spine.from({ skeleton: "logoAtlasJson", atlas: "logoAtlas" });
    this._logo.skeleton.setSkinByName("default");
    this._logo.state.setAnimation(0, "idle", true);

    this._holdWinContainer = new PIXI.Container();
    this._holdWinContainer.zIndex = 1;

    this._holdWinText = new PIXI.Text({
      text: "WON\nX0.00",
      style: {
        fontSize: 100,
        fontFamily: "gameFont",
        align: "center",
        fill: 0xffbb1e,
        stroke: {
          color: 0x000000,
          width: 12,
          join: "round",
        },
        letterSpacing: 0,
        padding: 4,
      }
    })
    this._holdWinText.anchor.set(0.5);

    const gameAtlasSpritesheet1 =
      Assets.getAsset<TGameAssetAlias>("atlasGame1");
    this._holdWinPanel = new PIXI.Sprite(
      gameAtlasSpritesheet1.textures["fs_panel_info.webp"],
    );
    this._holdWinPanel.anchor.set(0.5);

    this._holdWinContainer.visible = false;
  }

  private _parentAssets(): void {
    this.view.addChild(this._logo);
    this.view.addChild(this._holdWinContainer);
    this.view.addChild(this._backgroundHorizontalMain);

    this._holdWinContainer.addChild(this._holdWinPanel);
    this._holdWinContainer.addChild(this._holdWinText);
  }

  private _initializeComponents(): void {
    Symbol.createPool(20);

    this._gameUi = new GameUI();

    this._symbolPanel = new SymbolPanel();
    this._cellPanel = new CellPanel();
    this._cellPanel.view.visible = false;

    this._jackpotTierViews = [
      new JackpotTierView(EJackpotTier.MINOR),
      new JackpotTierView(EJackpotTier.MIDI),
      new JackpotTierView(EJackpotTier.MAJOR),
      new JackpotTierView(EJackpotTier.GRAND),
    ];
    for (const jackpotTierView of this._jackpotTierViews) {
      jackpotTierView.view.zIndex = 1;
    }

    this._winView = new WinView();
    this._winView.backgroundView.zIndex = 2;
    this._winView.view.zIndex = 3;

    this._freespinIntroView = new FreespinIntroView();
    this._freespinIntroView.view.visible = false;
    this._freespinIntroView.view.zIndex = 20;

    this._freespinOutroView = new FreespinOutroView();
    this._freespinOutroView.view.visible = false;
    this._freespinOutroView.view.zIndex = 20;

    this._freespinCounterView = new FreespinCounterView();
    this._freespinCounterView.view.visible = false;

    this._coinAnimator = new CoinAnimator();
    this._coinAnimator.view.zIndex = 2;

    this._bonusBackground = new BonusBackground();
    this._bonusBackground.fadeOut(true);
  }

  private _parentComponents(): void {
    this.view.addChild(this._bonusBackground.view);

    for (const jackpotTierView of this._jackpotTierViews) {
      this.view.addChild(jackpotTierView.view);
    }

    this.view.addChild(this._symbolPanel.view);
    this.view.addChild(this._cellPanel.view);

    this.view.addChild(this._freespinCounterView.view);
    this.view.addChild(this._winView.backgroundView);
    this.view.addChild(this._coinAnimator.view);

    this.view.addChild(this._freespinIntroView.view);
    this.view.addChild(this._freespinOutroView.view);

    this.view.addChild(this._winView.view);

    this.view.addChild(this._gameUi.view);
  }

  private _initializeScreenClickableArea(): void {
    const screenClickableAreaContainer: PIXI.Container = new PIXI.Container();
    const screenClickableArea: PIXI.Graphics = new PIXI.Graphics();
    screenClickableArea
      .rect(-960, -960, 1920, 1920)
      .fill({ color: 0xffffff, alpha: 0 });

    screenClickableAreaContainer.zIndex = 0;
    screenClickableAreaContainer.eventMode = "static";

    screenClickableAreaContainer.on(
      "pointerdown",
      (_: PIXI.FederatedPointerEvent) => {
        EventSystem.invoke(constants.UIEvents.CLICKED_INTERACTABLE_AREA);
      },
    );

    screenClickableAreaContainer.addChild(screenClickableArea);
    this.view.addChild(screenClickableAreaContainer);
  }

  private _onLandscapeLayoutRequested(): void {
    this._isLandscape = true;

    this._backgroundHorizontalMain.scale.set(1.75);
    this._backgroundHorizontalMain.position.set(0, 0);
    this._bonusBackground.view.scale.set(2.1);
    this._bonusBackground.view.position.set(0, -30);

    const ratio: number = window.innerWidth / window.innerHeight;
    // Second max value seems to do jack shit
    const k: number = Math.max(1.75 / ratio, 1.0);

    this._logo.scale.set(0.1);
    this._logo.position.set(-770 / k, -400);
    this._logo.zIndex = 1;

    const jackpotViewCenterOffsetX: number = -800;

    // Minor Tier
    this._jackpotTierViews[0].view.position.set(
      (jackpotViewCenterOffsetX - 5) / k,
      280,
    );
    this._jackpotTierViews[0].view.scale.set(0.6);

    // Midi Tier
    this._jackpotTierViews[1].view.position.set(
      (jackpotViewCenterOffsetX - 10) / k,
      130,
    );
    this._jackpotTierViews[1].view.scale.set(0.6);

    // Major Tier
    this._jackpotTierViews[2].view.position.set(
      (jackpotViewCenterOffsetX - 15) / k,
      -40,
    );
    this._jackpotTierViews[2].view.scale.set(0.6);

    // Grand Tier
    this._jackpotTierViews[3].view.position.set(
      (jackpotViewCenterOffsetX - 20) / k,
      -200,
    );
    this._jackpotTierViews[3].view.scale.set(0.6);

    this._freespinCounterView.view.position.set(300, -330);
    this._freespinCounterView.view.scale.set(0.275);
    this._holdWinContainer.position.set(-5.0, -330);
    this._holdWinContainer.scale.set(0.275);

    if (gameEngine.gameActionData) {
      switch (gameEngine.gameActionData.state) {
        case EGameState.MAIN_TO_HOLD:
        case EGameState.HOLD_TO_MAIN:
        case EGameState.HOLD:
          this._freespinCounterView.view.position.set(300, -420);
          this._holdWinContainer.position.set(-5, -420);
          break;
      }
    }

    this._symbolPanel.view.scale.set(1.3);
    this._symbolPanel.view.position.set(0, -120);

    this._cellPanel.view.scale.set(1.1);
    this._cellPanel.view.position.set(0, -50);
  }

  private _onPortraitLayoutRequested(): void {
    this._isLandscape = false;

    this._backgroundHorizontalMain.scale.set(2.5);
    this._backgroundHorizontalMain.position.set(400, -200);
    this._bonusBackground.view.scale.set(3);
    this._bonusBackground.view.position.set(450, -250);

    const ratio: number = window.innerWidth / window.innerHeight;
    const k: number = Math.max(1.4 / ratio, 0.5);

    let baseY = -600;
    const centerY = -200;
    const transitionThreshold = 0.65;

    if (gameEngine.gameActionData) {
      switch (gameEngine.gameActionData.state) {
        case EGameState.MAIN_TO_HOLD:
        case EGameState.HOLD_TO_MAIN:
        case EGameState.HOLD:
          baseY = -850;
          break;
      }
    }

    let logoY = baseY;
    let logoScale = 0.11;

    if (ratio <= transitionThreshold) {
      // Calculate how far below 0.4 we are (0 = at threshold, 1 = at ratio 0)
      const progress = Math.min(
        1,
        (transitionThreshold - ratio) / transitionThreshold,
      );
      // Interpolate between baseY and centerY
      logoY = baseY + (centerY - baseY) * progress;
      logoScale = 0.11 + (0.01 - 0.11) * progress;
    }

    this._logo.position.set(5, logoY);
    this._logo.scale.set(logoScale);
    this._logo.zIndex = 1;

    // Major Tier
    this._jackpotTierViews[2].view.position.set(1050 / k, -850);
    this._jackpotTierViews[2].view.scale.set(Math.max(0.85 / k, 0.6));

    // Grand Tier
    this._jackpotTierViews[3].view.position.set(-1050 / k, -850);
    this._jackpotTierViews[3].view.scale.set(Math.max(0.85 / k, 0.6));

    // Minor Tier
    this._jackpotTierViews[0].view.scale.set(Math.max(0.85 / k, 0.6));
    this._jackpotTierViews[0].view.position.set(
      this._jackpotTierViews[2].view.x - Math.max(300 / k, 210),
      -850,
    );
    if (ratio <= 0.4) {
      this._jackpotTierViews[0].view.position.set(
        this._jackpotTierViews[2].view.x,
        -700,
      );
    }

    // Midi Tier
    this._jackpotTierViews[1].view.scale.set(Math.max(0.85 / k, 0.6));
    this._jackpotTierViews[1].view.position.set(
      this._jackpotTierViews[3].view.x + Math.max(300 / k, 210),
      -850,
    );
    if (ratio <= 0.4) {
      this._jackpotTierViews[1].view.position.set(
        this._jackpotTierViews[3].view.x,
        -700,
      );
    }

    this._freespinCounterView.view.position.set(-310, 450);
    this._freespinCounterView.view.scale.set(0.35);
    this._holdWinContainer.position.set(-5, 450);
    this._holdWinContainer.scale.set(0.35);

    const maxSymbolPanelScale: number = 1.7;
    const maxCellPanelScale: number = 1.6;
    const maxPanelScaleThresholdRatio: number = 0.65;

    this._symbolPanel.view.position.set(15, -310);
    this._cellPanel.view.position.set(5, -300);

    if (ratio >= maxPanelScaleThresholdRatio) {
      this._symbolPanel.view.scale.set(maxSymbolPanelScale);
      this._cellPanel.view.scale.set(maxCellPanelScale);
    } else {
      this._symbolPanel.view.scale.set(
        (ratio / maxPanelScaleThresholdRatio) * maxSymbolPanelScale,
      );
      this._cellPanel.view.scale.set(
        (ratio / maxPanelScaleThresholdRatio) * maxCellPanelScale,
      );
      this._symbolPanel.view.y =
        -310 + (1 - ratio / maxPanelScaleThresholdRatio) * 100;
      this._cellPanel.view.y =
        -300 + (1 - ratio / maxPanelScaleThresholdRatio) * 100;
    }
  }

  private _onDeviceChanged(deviceType: TDeviceType): void {
    switch (deviceType) {
      case "desktop":
        this._gameUi.view.y = 0;
        break;
      case "mobile":
        this._gameUi.view.y = -60;
        break;
    }
  }
}
